import { useIsShowRequestSampleCode } from '@/business-components/request-sample-code/hooks/useIsShowRequestSampleCode';
import { RequestSampleCode } from '@/business-components/request-sample-code/RequestSampleCode';
import { ResponseExample } from '@/business-components/response-example/ResponseExample';
import { useResponseParameterValue } from '@/business-components/response-parameter/hooks/useResponseParameterValue';
import { useCalcDocContentPaddingTop } from '@/hooks/useCalcDocContentPaddingTop';
import { useCalcNavbarHeight } from '@/hooks/useCalcNavbarHeight';
import { useCalcNotificationHeight } from '@/hooks/useCalcNotificationHeight';
import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import type { HttpApiServerType } from '@/services/http-api/httpApi';
import { ApiDocLayout } from '@/shared/constant/layout/ApiDocLayout';
import type { ResourceHttpApiData } from '@/shared/constant/ResourceData';
import { ParameterDisplayMode } from '@tarslib/types/lib/projectSetting';
import clsx from 'clsx';
import React from 'react';

export type RightSampleCodeProps = {
  value: HttpApiServerType;
  extraData: ResourceHttpApiData['extraData'];
  className?: string;
};

export function RightSampleCode(props: RightSampleCodeProps) {
  const { className, value, extraData } = props;
  const apiDocLayout = useDocsDataModelStore((x) => x.docsBaseConfig.apiDocLayout);
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const isShowRequestSampleCode = useIsShowRequestSampleCode();
  const isOpenDebugPanel = useAppearanceConfigModelStore((x) => x.isOpenDebugPanel);

  const navbarHeight = useCalcNavbarHeight();
  const notificationHeight = useCalcNotificationHeight();
  const docPaddingTop = useCalcDocContentPaddingTop();

  const { responses } = useResponseParameterValue(value);
  const responseExamples = React.useMemo(() => {
    return (responses?.map((x) => x.responseExamples) ?? []).flat().filter(Boolean);
  }, [responses]);

  if (apiDocLayout !== ApiDocLayout.TwoColumn) {
    return null;
  }
  if (!isShowRequestSampleCode && !responseExamples?.length) {
    return null;
  }
  return (
    <div
      className={clsx('pointer-events-none', {
        ['@[850px]/http-api-content:flex @[850px]/http-api-content:justify-end']: !isOpenDebugPanel,
      })}
    >
      <div
        className={clsx(
          'pointer-events-auto flex !h-auto flex-col gap-5 p-0',
          {
            ['@[850px]/http-api-content:fixed @[850px]/http-api-content:bottom-0 @[850px]/http-api-content:mr-[-440px] @[850px]/http-api-content:w-[440px] @[850px]/http-api-content:pb-5 @[850px]/http-api-content:pl-5']:
              !isOpenDebugPanel,
          },
          className,
        )}
        style={{
          top: navbarHeight + notificationHeight + docPaddingTop,
        }}
      >
        <RequestSampleCode
          className={clsx({
            ['@[850px]/http-api-content:max-h-full @[850px]/http-api-content:min-h-[170px] @[850px]/http-api-content:shrink']:
              !isOpenDebugPanel,
          })}
          mode={ParameterDisplayMode.MODERN}
          value={value}
          extraData={extraData}
        />

        {!!responses?.length && (
          <ResponseExample
            mode={ParameterDisplayMode.MODERN}
            title={
              <span>
                <span className="hidden @[850px]/http-api-content:block">
                  {clientI18n('response.example.title.right')}
                </span>
                <span className="block @[850px]/http-api-content:hidden">
                  {clientI18n('response.example.title.center')}
                </span>
              </span>
            }
            value={responses}
            className={clsx('flex flex-col overflow-auto', {
              ['@[850px]/http-api-content:min-h-[170px] @[850px]/http-api-content:shrink']:
                !isOpenDebugPanel,
            })}
          />
        )}
      </div>
    </div>
  );
}
